import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import appApi from "api";
import SurveyResults from "components/Survey/SurveyResults";
import { PreferredContactMethod } from "@fhc/shared/constants";
import { removeAnchorLink } from "utils";
import SurveyResultsNotFound from "components/Survey/SurveyResultsNotFound.js";

const FunctionalSurveyResults = ({ isIframe = false }) => {
    const navigate = useNavigate();
    const { surveyLinkKey } = useParams();

    // Loading when the page renders because we need to wait until we get data
    const [loading, setLoading] = useState(true);
    const [appointment, setAppointment] = useState(null);
    const [surveyResults, setSurveyResults] = useState(null);
    const [collector, setCollector] = useState(null);
    const [surveyResultsNotFound, setSurveyResultsNotFound] = useState(false);
    const [questionResponses, setQuestionResponses] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [themeColors, setThemeColors] = useState(null);

    const [searchParams, _] = useSearchParams();
    const resultUuid = searchParams.get("uuid");

    useEffect(() => {
        if (themeColors === null) return;
        document.title = themeColors.appName;
    }, [themeColors]);

    useEffect(() => {
        async function getResults() {
            try {
                setLoading(true);

                const response = await appApi.get("surveys/results", {
                    params: {
                        result_uuid: resultUuid,
                        link_key: surveyLinkKey,
                    },
                });

                setSurveyResults(response.data.surveyResult);
                setCollector(response.data.collector);
                setQuestionResponses(response.data.questionResponses);
                setAppointment(response.data.appointment);
                setCompanyId(response.data.company_id);
                setThemeColors(response.data.theme);
            } catch (error) {
                //some errors still give us a theme
                const theme = error.response?.data?.theme;
                if (theme) setThemeColors(theme);

                // 400 for missing surveyUuid, 404 for surveyUuid not found
                // both cases can be treated as not found in the ui
                if (error.response?.status === 404 || error.response?.status === 400) {
                    setSurveyResultsNotFound(true);
                }
            } finally {
                setLoading(false);
            }
        }

        getResults();
    }, [resultUuid, surveyLinkKey]);

    async function resetAssessment() {
        try {
            await appApi.put(`/surveys/reset/${resultUuid}`, { isIframe }); //TODO update this call on the backend
            navigate(`/survey/${surveyLinkKey}`); // Navigate to the start of the survey
        } catch (error) {
            console.error(error);
        }
    }

    console.debug("company id:  ", companyId);

    async function bookAppointment(formData) {
        try {
            const response = await appApi.post("/appointments/book", {
                company_id: companyId,
                survey_result_uuid: resultUuid,
                question_response_id: questionResponses.id,
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                preferred_contact_method: formData.by_phone
                    ? PreferredContactMethod.PHONE
                    : PreferredContactMethod.EMAIL,
                postal_code: formData.postal_code,
                phone_number: formData.phone_number,
                subscribe_to_other_resources: formData.subscribe_to_other_resources,
                result_url: removeAnchorLink(window.location.href),
                survey_link_key: surveyLinkKey,
            });

            setAppointment(response.data.appointment);
        } catch (error) {
            if (error.response?.data?.appointment?.mail_to) {
                setAppointment(error.response?.data?.appointment);
            } else {
                // An internal error has occurred and we've already tried to send an email, and provide a mailto link.
                toast.error(
                    "We're sorry, an error has occurred while booking your appointment. Please try again later.",
                );
            }
            console.error(error);
        }
    }

    async function emailResults(email) {
        const response = await appApi.post(`/survey/sendEmail/${resultUuid}`, {
            email,
            link: removeAnchorLink(window.location.href),
        });

        if (response.data.success) {
            setSurveyResults((results) => ({ ...results, ...response.data.results }));
        }

        return response.data.success;
    }

    return loading ? null : surveyResultsNotFound ? (
        <SurveyResultsNotFound themeColors={themeColors} resetAssessment={resetAssessment} />
    ) : (
        <SurveyResults
            themeColors={themeColors}
            questionResponses={questionResponses}
            surveyResults={surveyResults}
            appointment={appointment}
            resetAssessment={resetAssessment}
            bookAppointment={bookAppointment}
            emailResults={emailResults}
            collector={collector}
        />
    );
};

export default FunctionalSurveyResults;
